import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import {
  useHasAccessToDisplayMedia,
  useHasAccessToLiveVideo,
  useHasAccessToRequestMedia,
} from '../components/AdditionalData/AdrQuery/selectors/selectors';
import useShouldRenderTF911 from './useShouldRenderTF911';

/**
 * Hook to determine whether or not to render the Media component
 * @returns Boolean
 */
const useShouldRenderMedia = () => {
  const selectedCall = useSelectedCall();

  const hasAccessToDisplayMedia = useHasAccessToDisplayMedia();
  const hasAccessToLiveVideo = useHasAccessToLiveVideo();
  const hasAccessToRequestMedia = useHasAccessToRequestMedia();
  const mostRecentLoc = getMostRecentLoc(selectedCall);

  const shouldRender = useMemo(() => {
    return (
      mostRecentLoc?.application?.toLowerCase() === 'ios' &&
      (hasAccessToLiveVideo ||
        hasAccessToRequestMedia ||
        hasAccessToDisplayMedia)
    );
  }, [
    hasAccessToDisplayMedia,
    hasAccessToLiveVideo,
    hasAccessToRequestMedia,
    mostRecentLoc,
  ]);

  return shouldRender;
};

/**
 * Hook to determine whether or not to render the different Chat components
 * @returns Boolean
 */
export const useIsAnyChatEnabled = () => {
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const isCallShared = useSelector(
    state =>
      state.emergencyCalls.callQueue[selectedCallerID]?.agencyShareChat
        ?.isCallShared,
  );
  const isCallReceived = useSelector(
    state =>
      state.emergencyCalls.callQueue[selectedCallerID]?.agencyShareChat
        ?.isCallReceived,
  );

  const isTF911Enabled = useIsTF911Enabled();
  const shouldRenderTF911 = useShouldRenderTF911(selectedCallerID);

  const isAnyChatEnabled = useMemo(() => {
    return (
      isCallReceived || isCallShared || (isTF911Enabled && shouldRenderTF911)
    );
  }, [isCallReceived, isCallShared, isTF911Enabled, shouldRenderTF911]);

  return isAnyChatEnabled;
};

export const useIsSelectedCallerIDOutboundSMS = () => {
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const textFrom911Conversation =
    useSelectedTextFrom911Conversation(selectedCallerID);

  const isOutboundSMS = textFrom911Conversation?.data?.isOutboundSMS;
  return isOutboundSMS;
};

// TODO: useShouldRenderCallSplitPane should be converted to createSelector
/**
 * Hook to determine whether or not to render the split pane for the call pane
 * @returns Boolean
 */
const useShouldRenderCallSplitPane = () => {
  const shouldRenderMedia = useShouldRenderMedia();
  const isAnyChatEnabled = useIsAnyChatEnabled();

  const listOfBoolConditions = useMemo(
    () => [shouldRenderMedia, isAnyChatEnabled],
    [shouldRenderMedia, isAnyChatEnabled],
  );

  return useMemo(() => {
    return listOfBoolConditions.some(bool => !!bool);
  }, [listOfBoolConditions]);
};

export default useShouldRenderCallSplitPane;
